import { useState, useEffect } from 'react';
import { Box, Modal, Skeleton } from '@mui/material';
import { colors } from '@src/styles';
import { blurBackdropSX, skeletonSX } from '../scriptsStyles';
import { getActionById, triggerAction } from '../hooks/scripts';
import { ModalHeader, ActionDetailsContent, ModalActions } from './modal';
import { useError } from '@src/contexts/ErrorContext';
import { ActionDTO } from '@src/services/tools/modules/v1/scripts/dto/tools-requests.dto';

interface ResponseDetailsModalProps {
  open: boolean;
  onClose: () => void;
  scriptId: string;
  executionId: string;
  actionId: string | null;
}

const modalBoxStyles = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(880px, 94vw)',
  maxHeight: '88vh',
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
  bgcolor: colors.brand.surface,
  color: colors.primary.white,
  border: `1px solid ${colors.brand.line}`,
  borderRadius: '16px',
  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5)',
  outline: 'none',
};

const ActionDetailsSkeleton = () => (
  <Box aria-busy="true" sx={{ px: 3, py: 2.5 }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
      {[0, 1, 2, 3].map((item) => (
        <Box key={item}>
          <Skeleton variant="text" width={70} height={14} sx={skeletonSX} />
          <Skeleton variant="text" width="80%" height={20} sx={skeletonSX} />
        </Box>
      ))}
    </Box>
    <Skeleton variant="rounded" width={180} height={30} sx={{ ...skeletonSX, borderRadius: '8px', mb: 1.5 }} />
    <Skeleton variant="rounded" height={260} sx={{ ...skeletonSX, borderRadius: '12px' }} />
  </Box>
);

export const ResponseDetailsModal = ({ open, onClose, scriptId, executionId, actionId }: ResponseDetailsModalProps) => {
  const [actionData, setActionData] = useState<ActionDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);
  const { setError, setSuccess } = useError();

  useEffect(() => {
    const fetchActionData = async () => {
      if (!open || !executionId || !actionId || !scriptId) {
        setActionData(null);
        return;
      }

      setIsLoading(true);
      try {
        const data = await getActionById(executionId, actionId);
        setActionData(data);
      } catch (error) {
        console.error('Error fetching action details:', error);
        setError({
          title: 'Erro ao buscar detalhes da ação',
          message: error?.response?.data?.message || error?.message || 'Não foi possível buscar os detalhes da ação',
          statusCode: error?.response?.status,
        });
        setActionData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActionData();
  }, [open, scriptId, actionId]);

  const handleTriggerAction = async () => {
    if (!actionId) return;

    setIsTriggering(true);
    try {
      const response = await triggerAction(scriptId, actionId);
      setSuccess({
        title: 'Ação disparada com sucesso',
        message: `A ação foi disparada com sucesso`,
      });
      try {
        const refreshed = await getActionById(executionId, actionId);
        setActionData(refreshed);
      } catch (reloadErr) {
        console.error('Error reloading action after trigger:', reloadErr);
        setActionData(response);
      }
    } catch (error) {
      console.error('Error triggering action:', error);
      setError({
        title: 'Erro ao disparar ação',
        message: error?.response?.data?.message || error?.message || 'Não foi possível disparar a ação',
        statusCode: error?.response?.status,
      });
    } finally {
      setIsTriggering(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="response-details-modal"
      slotProps={{ backdrop: { sx: blurBackdropSX } }}
    >
      <Box sx={modalBoxStyles}>
        <ModalHeader title="Detalhes da action" onClose={onClose} actionData={isLoading ? null : actionData} />

        {isLoading ? (
          <ActionDetailsSkeleton />
        ) : actionData ? (
          <>
            <ActionDetailsContent actionData={actionData} />
            <ModalActions
              onClose={onClose}
              onTrigger={handleTriggerAction}
              isLoading={isTriggering}
              isAlreadyTriggered={['CONCLUDED', 'RUNNING', 'WAITING'].includes(actionData.status)}
            />
          </>
        ) : null}
      </Box>
    </Modal>
  );
};
